/**
 * Frontend TypeScript type definitions
 */

export enum UserRole {
  PLAYER = 'player',
  COACH = 'coach',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  ACADEMY_ADMIN = 'academy_admin',
  EVALUATION_ADMIN = 'evaluation_admin',
  CONTEST_ADMIN = 'contest_admin',
  SPONSOR = 'sponsor',
}

export enum GenderType {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

export enum SkillLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
  PROFESSIONAL = 'professional',
}

export enum AgeCategory {
  U9 = 'U9',
  U11 = 'U11',
  U13 = 'U13',
  U15 = 'U15',
  U17 = 'U17',
  U19 = 'U19',
  SENIOR = 'Senior',
  MASTER = 'Master',
  VETERAN = 'Veteran',
}

export enum SpeedLevel {
  SLOW = 'slow',
  MEDIUM = 'medium',
  FAST = 'fast',
  VERY_FAST = 'very_fast',
}

export enum RankingCategory {
  SELF_RATED = 'self_rated',
  COACH_EVALUATED = 'coach_evaluated',
  PEER_COACH = 'peer_coach',
  MATCH_BASED = 'match_based',
  RALLY_TARGET = 'rally_target',
  SHOT_PERFORMANCE = 'shot_performance',
  COMPOSITE = 'composite',
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  is_email_verified: boolean;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
  // Coach specific fields
  coachId?: string;
  coachIdCode?: string;
  coachLevel?: string;
  // Academy (coach) fields
  academyId?: string;
  academyName?: string;
  academyDescription?: string;
  academyAddress?: string;
  academyContact?: string;
  // Player specific fields
  playerId?: string;
  playerIdCode?: string;
  // Sponsor specific fields
  sponsorId?: string;
  companyName?: string;
  profileStatus?: string;
}

export interface Player {
  id: string;
  user_id: string;
  player_id_code: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: GenderType;
  phone?: string;
  age_category: string;
  skill_level: SkillLevel;
  playing_style?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  profile_photo_url?: string;
  // Parent information
  parentEmail?: string;
  parentPhone?: string;
  parentCountry?: string;
  fatherName?: string;
  motherName?: string;
  registeredByParent?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Coach {
  id: string;
  user_id: string;
  coach_id_code: string;
  first_name: string;
  last_name: string;
  phone?: string;
  bio?: string;
  years_of_experience?: number;
  certifications?: any[];
  specializations?: string;
  academy_name?: string;
  is_verified: boolean;
  verification_date?: string;
  profile_photo_url?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface RegisterPlayerRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: GenderType;
  phone?: string;
  is_below_18?: boolean;
  parent_email?: string;
  parent_phone?: string;
  parent_country?: string;
  father_name?: string;
  mother_name?: string;
}

export interface RegisterCoachRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  bio?: string;
  years_of_experience?: number;
  certifications?: any[];
  specializations?: string;
  academy_name?: string;
}

export interface RegisterSponsorRequest {
  email: string;
  password: string;
  companyName: string;
  contactName: string;
  phone?: string;
  websiteUrl?: string;
  country?: string;
  industry?: string;
  preferredTier?: string;
  budgetBand?: string;
  sports?: string[];
  interestCategories?: string[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: any;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ShotEvaluation {
  id: string;
  player_id: string;
  shot_type_id: string;
  evaluator_id?: string;
  session_date: string;
  session_type: 'live_practice' | 'video_submission';
  total_attempts: number;
  successful_attempts: number;
  failed_attempts: number;
  accuracy_score: number;
  speed_level: SpeedLevel;
  speed_score: number;
  overall_score?: number;
  video_url?: string;
  is_validated: boolean;
  validated_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Tournament {
  id: string;
  name: string;
  description?: string;
  tournament_type: 'singles' | 'doubles' | 'mixed_doubles';
  age_category: string;
  start_date: string;
  end_date: string;
  registration_start_date: string;
  registration_end_date: string;
  venue?: string;
  max_participants?: number;
  match_format?: string;
  tournament_format: 'knockout' | 'round_robin' | 'hybrid';
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerRanking {
  id: string;
  player_id: string;
  ranking_category: RankingCategory;
  score: number;
  rank_position?: number;
  age_category: string;
  evaluated_by?: string;
  evaluation_date: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}


